import { z } from 'zod';
import { HttpClient } from '@sisense/sdk-rest-client';
import { DataSourceWithStatus } from '@/services/data-sources/data-sources-service';

/**
 * Canonical tool description for getDataSources — reusable by any host (sdk-assistant, MCP server, custom integrations).
 * Hosts may append host-specific notes but should not duplicate or contradict this text.
 */
export const getDataSourcesToolDescription = `Get the available data sources / data models. "Data sources" and "data models" are the SAME entity — this is the only discovery tool for both.

Before promising the user that queries, charts, dashboards, or query recommendations will work on a source, use this tool (or rely on a fresh result from the current conversation) to see what exists and whether each item is ready to query or still a draft.

Returns:
- dataSourceTitles: every source the user has access to (ready + draft)
- draftDataSourceTitles: subset that are DRAFT (existing data models that have never been built — present in metadata but no successful build yet)
- dataSources: array of { title, oid, status, live }, where:
    * oid is the ELASTICUBE DATAMODEL UUID — pass this as datamodelOid to getModelSchema, addRelation, createPerspective, updatePerspective, listPerspectives, triggerBuild. NEVER pass the title as datamodelOid.
    * status is 'ready' (built/queryable) or 'draft' (not built yet)
    * live is true for live/external connections (Snowflake, BigQuery, etc.) — these are NOT elasticubes and have no oid; they cannot be used with schema/perspective tools.

Workflow rules for drafts:
- DRAFTS CANNOT BE QUERIED. Do NOT call buildQuery, buildChart, buildDashboard, or recommendQueries on a draft — the query endpoints will reject it.
- Drafts CAN be inspected (getModelSchema), have relations edited (addRelation), and have perspectives listed/created (listPerspectives, createPerspective) — these are metadata operations.
- "Show me / view / explore [draft model name]" → call getModelSchema with that entry's oid IMMEDIATELY. Do not ask the user "schema or build?" — just render the diagram. Only block on QUERY actions.
- If the user asks to query/chart/analyze a draft, tell them it must be built first and offer to call triggerBuild.

When listing sources to the user, mark drafts explicitly (e.g. "Sales DB (draft — not built yet)").`;

/**
 * Schema for getDataSources engine parameters
 */
export const getDataSourcesSchema = z.object({});

/**
 * Context interface for getDataSourcesEngine
 */
export interface GetDataSourcesContext {
	/**
	 * Tool call ID
	 */
	toolCallId: string;
	httpClient?: HttpClient;
}

/**
 * Result from getDataSourcesEngine
 */
export interface DataSourcesResult {
	/**
	 * List of data source titles (includes both ready and draft)
	 */
	dataSourceTitles: string[];
	/**
	 * Subset of titles that are draft (existing models that have never been built — not queryable yet)
	 */
	draftDataSourceTitles: string[];
	/**
	 * Full data source objects with `status: 'ready' | 'draft'` marker
	 */
	dataSources: DataSourceWithStatus[];
}
